import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Activity, ArrowUpRight, Clock3, RefreshCw, ShieldCheck, Users, XCircle, CheckCircle2, Radio, CreditCard } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';

const FILTERS = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'resubmit', label: 'Resubmit' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'incomplete', label: 'Incomplete' },
  { key: '', label: 'All' },
];

const STATUS_META = {
  pending: ['Pending', 'warning'],
  approved: ['Approved', 'success'],
  resubmit: ['Needs action', 'danger'],
  rejected: ['Rejected', 'danger'],
  incomplete: ['Incomplete', 'neutral'],
};

export default function AdminDashboard() {
  const { me } = useAuth();
  const [stats, setStats] = useState(null);
  const [providers, setProviders] = useState([]);
  const [services, setServices] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);

  const can = (permission) => me?.is_owner || me?.permissions?.includes(permission);

  const load = async () => {
    setLoading(true);
    try {
      const requests = [api.get('/admin/stats')];
      if (can('providers')) {
        requests.push(api.get(filter ? `/admin/providers?status=${filter}` : '/admin/providers'));
      } else {
        requests.push(Promise.resolve({ data: { providers: [] } }));
      }
      if (can('services')) {
        requests.push(api.get('/admin/services'));
      } else {
        requests.push(Promise.resolve({ data: { services: [] } }));
      }

      const [s, p, svc] = await Promise.all(requests);
      setStats(s.data);
      setProviders(p.data.providers || []);
      setServices(svc.data.services || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load operations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter, me?.is_owner, me?.permissions?.join(',')]);

  const serviceSummary = useMemo(() => {
    const active = services.filter((s) => s.broadcast_active).length;
    const total = services.length;
    return { active, total };
  }, [services]);

  return (
    <div className="admin-page">
      <div className="admin-page-head admin-dashboard-head">
        <div>
          <div className="admin-eyebrow"><Activity size={13} /> Command centre</div>
          <h1>Good to see you, {me?.is_owner ? 'Owner' : (me?.display_name?.split(' ')[0] || 'Operator')}.</h1>
          <p>Everything important, one operational view. Work from the queue — not from a maze of screens.</p>
        </div>
        <button className="admin-lux-button secondary" onClick={load} disabled={loading}><RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh</button>
      </div>

      {stats && (
        <motion.div className="admin-dashboard-grid" initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.055 } }, hidden: {} }}>
          <MetricCard label="Total providers" value={stats.providers.total} icon={Users} tone="blue" />
          <MetricCard label="Pending KYC" value={stats.providers.pending} icon={Clock3} tone="amber" />
          <MetricCard label="Approved" value={stats.providers.approved} icon={ShieldCheck} tone="green" />
          <MetricCard label="Rejected" value={stats.providers.rejected} icon={XCircle} tone="red" />
          <MetricCard label="Consumers" value={stats.consumers.total} icon={Users} tone="slate" />
          <MetricCard label="Active services" value={serviceSummary.total ? `${serviceSummary.active}/${serviceSummary.total}` : '—'} icon={Radio} tone="violet" />
        </motion.div>
      )}

      <div className="admin-command-grid">
        {can('providers') ? (
        <div className="admin-card admin-card-tall">
          <div className="admin-card-head">
            <div><h2>Provider approval queue</h2><p>Review the next important applications without opening five tabs.</p></div>
            <span className="admin-live-tag">{providers.length} visible</span>
          </div>
          <div className="admin-filter-row">
            {FILTERS.map((f) => (
              <button key={f.key || 'all'} className={filter === f.key ? 'active' : ''} onClick={() => setFilter(f.key)}>{f.label}</button>
            ))}
          </div>
          <div className="admin-list">
            {providers.map((provider, index) => {
              const [label, tone] = STATUS_META[provider.approval_status] || ['Unknown', 'neutral'];
              return (
                <motion.a
                  href={`/admin/providers/${provider.id}`}
                  key={provider.id}
                  className="admin-provider-row"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.025 }}
                >
                  <div className="admin-table-avatar">{provider.name?.slice(0, 1)?.toUpperCase() || 'P'}</div>
                  <div className="admin-provider-main">
                        <strong>{provider.name || 'Unnamed provider'}</strong>
                        <span>{provider.category || 'Category not selected'} · {provider.city || 'City not set'} · {provider.current_address || 'Address pending'}</span>
                        <small className="admin-provider-ref">{provider.reference_number || 'Reference pending'}</small>
                      </div>
                  <span className={`admin-status ${tone}`}>{label}</span>
                  <ArrowUpRight size={16} className="admin-row-arrow" />
                </motion.a>
              );
            })}
            {!loading && providers.length === 0 && <div className="admin-empty"><CheckCircle2 size={20} /> No applications in this filter.</div>}
          </div>
          <div className="admin-card-footer"><span>Tip: incomplete and resubmit queues need action fastest.</span></div>
        </div>
        ) : (
          <div className="admin-card admin-card-tall">
            <div className="admin-card-head">
              <div><h2>Provider queue</h2><p>This workspace is hidden for your access level.</p></div>
            </div>
            <div className="admin-empty">Ask the RESQLY main account to grant Providers & KYC access.</div>
          </div>
        )}

        <div className="admin-stack">
          <QuickCard icon={Activity} title="Live operations" body="Watch active bookings, payment state and provider assignment." href="/admin/operations" permission={me?.is_owner || me?.permissions?.includes('operations')} />
          <QuickCard icon={Users} title="Customers" body="Find customers and handle operational support without exposing medical records." href="/admin/customers" permission={me?.is_owner || me?.permissions?.includes('customers')} />
          <QuickCard icon={Radio} title="Dispatch control" body="Manage the broadcast and provider alert layer." href="/admin/provider-dispatch" permission={me?.is_owner || me?.permissions?.includes('dispatch')} />
          <QuickCard icon={CreditCard} title="Payment operations" body="Inspect payments, webhooks and reconciliation." href="/admin/payment-operations" permission={me?.is_owner || me?.permissions?.includes('payments')} />
          <QuickCard icon={WalletIcon} title="Finance & payouts" body="Control pricing, commission and payout settings." href="/admin/financial-config" permission={me?.is_owner || me?.permissions?.includes('finance')} />
        </div>
      </div>

      {can('services') && (
      <div className="admin-card">
        <div className="admin-card-head">
          <div><h2>Service pulse</h2><p>One glance at what RESQLY is currently accepting.</p></div>
          <span className="admin-live-tag"><span className="admin-live-dot" /> Live config</span>
        </div>
        <div className="admin-service-grid">
          {services.map((service) => (
            <div key={service.key} className={`admin-service-pulse ${service.broadcast_active ? 'on' : 'off'}`}>
              <div><span className="admin-pulse-dot" /><strong>{service.label || service.key}</strong></div>
              <span>{service.broadcast_active ? 'Broadcasting' : 'Paused'}</span>
            </div>
          ))}
          {!services.length && <div className="admin-empty">Service configuration is not available yet.</div>}
        </div>
      </div>
      )}

      <div className="admin-footer-note">
        <ShieldCheck size={15} /> Administrative changes are permission-scoped and audited. Owner access is never delegated.
      </div>
    </div>
  );
}

function MetricCard({ label, value, icon: Icon, tone }) {
  return (
    <motion.div
      className={`admin-metric-card tone-${tone}`}
      variants={{ hidden: { opacity: 0, y: 14 }, visible: { opacity: 1, y: 0 } }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="admin-metric-icon"><Icon size={18} /></div>
      <span>{label}</span>
      <strong>{value}</strong>
    </motion.div>
  );
}

function QuickCard({ icon: Icon, title, body, href, permission }) {
  if (!permission) return null;
  return (
    <motion.a href={href} className="admin-quick-card" whileHover={{ y: -5, x: 2 }} transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}>
      <div className="admin-quick-icon"><Icon size={18} /></div>
      <div><strong>{title}</strong><p>{body}</p></div>
      <ArrowUpRight size={17} className="ml-auto text-slate-400" />
    </motion.a>
  );
}

function WalletIcon(props) {
  return <CreditCard {...props} />;
}
